import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { initMetaPixelIfConsented } from "@/utils/metaPixel";

const STORAGE_KEY = "fg_cookie_consent";

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

export function useCookieConsent() {
  const raw = typeof window !== "undefined" ? (getCookie(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY)) : null;
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = getCookie(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const accept = (all) => {
    const value = JSON.stringify({
      accepted: true,
      analytics: all,
      marketing: all,
      personalization: all,
      timestamp: new Date().toISOString(),
    });
    // Save in both cookie (persists on mobile) and localStorage (fast reads)
    setCookie(STORAGE_KEY, value);
    localStorage.setItem(STORAGE_KEY, value);
    // Sync into privacy prefs so PrivacyChoices page reflects the same choice
    const existing = (() => {
      try { return JSON.parse(localStorage.getItem("fg_privacy_prefs") || "{}"); } catch { return {}; }
    })();
    localStorage.setItem(
      "fg_privacy_prefs",
      JSON.stringify({ ...existing, analytics: all, personalization: all, marketing: all, location: true })
    );
    // Load the Meta Pixel now if marketing was just granted (else it stays off).
    initMetaPixelIfConsented();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto bg-card border border-border rounded-2xl shadow-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Cookie className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground leading-relaxed">
            We use cookies to improve your experience and analyse platform usage. See our{" "}
            <Link to={createPageUrl("CookiePolicy")} className="text-primary hover:underline font-medium">
              Cookie Policy
            </Link>{" "}
            for details.{" "}
            <Link to={createPageUrl("PrivacyChoices")} className="text-primary hover:underline">
              Manage preferences
            </Link>
            .
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg flex-1 sm:flex-none"
            onClick={() => accept(false)}
          >
            Essential only
          </Button>
          <Button
            size="sm"
            className="rounded-lg flex-1 sm:flex-none"
            onClick={() => accept(true)}
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
